// Session persistence for the admin panel.
//
// The access token (24h) and the rotating refresh token (30 days) are stored
// together in the same storage so that a full page load or a second tab can
// resume without immediately spending a refresh-token rotation. Rotation is a
// one-shot operation on the server: any attempt to reuse an old token revokes
// the whole token family, so every avoided refresh removes a chance to lose
// the session (see src/auth/authClient.js for the single-flight refresh).

const STORAGE_KEYS = {
  accessToken: "accessToken",
  accessTokenExpiresAt: "accessTokenExpiresAt",
  refreshToken: "refreshToken",
  role: "role",
  agency: "agency",
  agencyId: "agencyId",
  platform: "platform",
  platformId: "platformId",
  organizationName: "organizationName",
  organizationId: "organizationId",
  organizationType: "organizationType",
};

export const SESSION_STORAGE_KEYS = Object.freeze({ ...STORAGE_KEYS });

// Treat a token as unusable this long before its real expiry so a request
// that is in flight at the boundary does not fail with 401 and refresh twice.
const ACCESS_TOKEN_EXPIRY_SKEW_MS = 60 * 1000;

const safeStorage = (storage) => {
  try {
    return storage;
  } catch {
    return null;
  }
};

const getShouldPersistSession = () => {
  try {
    return JSON.parse(localStorage.getItem("persist")) || false;
  } catch {
    return false;
  }
};

export const getStoredPersistPreference = getShouldPersistSession;

const getSessionStorageTarget = (persist = getShouldPersistSession()) =>
  persist ? safeStorage(localStorage) : safeStorage(sessionStorage);

const getSessionStorageFallback = (persist = getShouldPersistSession()) =>
  persist ? safeStorage(sessionStorage) : safeStorage(localStorage);

const getStoredValue = (key) => {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const removeStoredValue = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {}
  try {
    sessionStorage.removeItem(key);
  } catch {}
};

const setOrRemoveStorageValue = (storage, key, value) => {
  if (value === undefined || value === null || value === "") {
    removeStoredValue(key);
    return;
  }

  storage?.setItem(key, String(value));
};

// Reads the `exp` claim (seconds) of a JWT without verifying it. The server
// remains the authority; this is only used to decide whether a stored token is
// worth sending at all.
export const decodeJwtExpiryMs = (token) => {
  if (typeof token !== "string") {
    return 0;
  }
  const parts = token.split(".");
  if (parts.length < 2) {
    return 0;
  }
  try {
    const payloadSegment = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payloadSegment + "=".repeat((4 - (payloadSegment.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    const expiry = Number(payload?.exp);
    return Number.isFinite(expiry) ? expiry * 1000 : 0;
  } catch {
    return 0;
  }
};

export const isAccessTokenUsable = (token, expiresAtMs, now = Date.now()) => {
  if (!token) {
    return false;
  }
  const expiry = Number(expiresAtMs) || decodeJwtExpiryMs(token);
  if (!expiry) {
    // Unknown expiry: let the server decide (a 401 triggers one refresh).
    return true;
  }
  return expiry - ACCESS_TOKEN_EXPIRY_SKEW_MS > now;
};

export const buildAuthSessionState = ({
  tokens = {},
  user = {},
  agency,
  platform,
}) => {
  const organization =
    platform && platform._id
      ? {
          id: platform._id,
          name: platform.name,
          type: "platform",
        }
      : agency && agency._id
        ? {
            id: agency._id,
            name: agency.name,
            type: "agency",
          }
        : {
            id: "",
            name: "",
            type: "",
          };

  const accessToken = tokens?.accessToken || "";

  return {
    accessToken,
    accessTokenExpiresAt: accessToken ? decodeJwtExpiryMs(accessToken) : 0,
    refreshToken: tokens?.refreshToken || "",
    role: user?.role || "",
    agency: agency?.name || "",
    agencyId: agency?._id || "",
    platform: platform?.name || "",
    platformId: platform?._id || "",
    organizationName: organization.name || "",
    organizationId: organization.id || "",
    organizationType: organization.type || "",
  };
};

export const persistAuthSessionState = (
  sessionState,
  { persist = getShouldPersistSession() } = {},
) => {
  const targetStorage = getSessionStorageTarget(persist);
  const fallbackStorage = getSessionStorageFallback(persist);

  Object.values(STORAGE_KEYS).forEach((key) => {
    try {
      fallbackStorage?.removeItem(key);
    } catch {}
  });

  const accessTokenExpiresAt =
    sessionState.accessTokenExpiresAt ||
    (sessionState.accessToken ? decodeJwtExpiryMs(sessionState.accessToken) : 0);

  setOrRemoveStorageValue(
    targetStorage,
    STORAGE_KEYS.accessToken,
    sessionState.accessToken,
  );
  setOrRemoveStorageValue(
    targetStorage,
    STORAGE_KEYS.accessTokenExpiresAt,
    accessTokenExpiresAt || "",
  );
  setOrRemoveStorageValue(
    targetStorage,
    STORAGE_KEYS.refreshToken,
    sessionState.refreshToken,
  );
  setOrRemoveStorageValue(targetStorage, STORAGE_KEYS.role, sessionState.role);
  setOrRemoveStorageValue(
    targetStorage,
    STORAGE_KEYS.agency,
    sessionState.agency,
  );
  setOrRemoveStorageValue(
    targetStorage,
    STORAGE_KEYS.agencyId,
    sessionState.agencyId,
  );
  setOrRemoveStorageValue(
    targetStorage,
    STORAGE_KEYS.platform,
    sessionState.platform,
  );
  setOrRemoveStorageValue(
    targetStorage,
    STORAGE_KEYS.platformId,
    sessionState.platformId,
  );
  setOrRemoveStorageValue(
    targetStorage,
    STORAGE_KEYS.organizationName,
    sessionState.organizationName,
  );
  setOrRemoveStorageValue(
    targetStorage,
    STORAGE_KEYS.organizationId,
    sessionState.organizationId,
  );
  setOrRemoveStorageValue(
    targetStorage,
    STORAGE_KEYS.organizationType,
    sessionState.organizationType,
  );
};

export const readStoredAuthSession = () => {
  const accessToken = getStoredValue(STORAGE_KEYS.accessToken);
  const accessTokenExpiresAt = Number(
    getStoredValue(STORAGE_KEYS.accessTokenExpiresAt),
  );
  const usable = isAccessTokenUsable(accessToken, accessTokenExpiresAt);

  return {
    accessToken: usable ? accessToken : "",
    accessTokenExpiresAt: usable ? accessTokenExpiresAt : 0,
    refreshToken: getStoredValue(STORAGE_KEYS.refreshToken),
    role: getStoredValue(STORAGE_KEYS.role),
    agency: getStoredValue(STORAGE_KEYS.agency),
    agencyId: getStoredValue(STORAGE_KEYS.agencyId),
    platform: getStoredValue(STORAGE_KEYS.platform),
    platformId: getStoredValue(STORAGE_KEYS.platformId),
    organizationName: getStoredValue(STORAGE_KEYS.organizationName),
    organizationId: getStoredValue(STORAGE_KEYS.organizationId),
    organizationType: getStoredValue(STORAGE_KEYS.organizationType),
  };
};

export const clearStoredAuthSession = () => {
  Object.values(STORAGE_KEYS).forEach((key) => {
    removeStoredValue(key);
  });
};

export const getStoredRole = () => readStoredAuthSession().role;

export const getStoredRefreshToken = () =>
  readStoredAuthSession().refreshToken;

// Best-effort server-side revocation of the stored refresh token on logout,
// so the session cannot be resumed from a storage copy. Bounded so a slow or
// unreachable backend never blocks the local logout; the endpoint is
// idempotent and always answers 200.
export const revokeStoredRefreshToken = async (axiosInstance) => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return;
  }
  try {
    await Promise.race([
      axiosInstance.post("/auth/logout", { refresh_token: refreshToken }),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
  } catch {
    // Revocation is best-effort; local logout proceeds regardless.
  }
};
